import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/template/index.scss";
import "./assets/global.scss";
import "./assets/responsive.scss";
import "react-toastify/dist/ReactToastify.css";

import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ModalProvider } from "./context/ModalContext";
import PrivateRoute from "./utils/security/PrivateRoute";
import PublicRoute from "./utils/security/PublicRoute";
import ScrollToTop from "./components/Reusable/ScrollToTop";
import { ToastContainer } from "react-toastify";
import { checkAuth } from "./actions/user";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import FloatingSubscribeButton from "./components/Reusable/FloatingSubscribeButton";
import { fetchMySubscription } from "./actions/global";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { RegistrationPromptProvider } from "./context/RegistrationPromptContext";
import RegistrationPrompt from "./components/Reusable/RegistrationPrompt";
import Spinner from "./components/Reusable/Spinner";
import { pushUserData } from "./utils/tracking";
import { hasStatisticsConsent } from "./utils/cookieConsent";
import ChunkLoadErrorBoundary from "./components/Reusable/ChunkLoadErrorBoundary";
import {
  CAMPAIGN_SALES_PATHS,
  CAMPAIGN_SALES_ROUTES,
} from "./components/CampaignSales/campaignSalesRoutes";
import {
  buildCanonicalSalesPath,
  CANONICAL_SALES_PATH,
  LEGACY_SALES_PATHS,
} from "./constants/salesRoutes";

// Route-based code splitting — each page is a separate chunk
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./components/Reusable/Contact"));
const ContactChannel = lazy(() => import("./pages/ContactChannel"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const InstructorProfile = lazy(() => import("./pages/InstructorProfile"));
const MasterkatCoursePlayer = lazy(() => import("./pages/MasterkatCoursePlayer"));
const MasterkatGrid = lazy(() => import("./pages/MasterkatGrid"));
const MasterkatLanding = lazy(() => import("./pages/MasterkatLanding"));
const MasterkatLanding2 = lazy(() => import("./pages/MasterkatLanding2"));
const MasterkatPlayer = lazy(() => import("./pages/MasterkatPlayer"));
const MikrokatGrid = lazy(() => import("./pages/MikrokatGrid"));
const MikrokatPlayer = lazy(() => import("./pages/MikrokatPlayer"));
const MinikatGrid = lazy(() => import("./pages/MinikatGrid"));
const MinikatLanding = lazy(() => import("./pages/MinikatLanding"));
const MinikatPlayer = lazy(() => import("./pages/MinikatPlayer"));
const OurInstructors = lazy(() => import("./pages/OurInstructors"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Profile = lazy(() => import("./pages/Profile"));
const ReelsLanding = lazy(() => import("./pages/ReelsLanding"));
const Search = lazy(() => import("./pages/Search"));
const Terms = lazy(() => import("./pages/Terms"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const VerifyAccount = lazy(() => import("./pages/VerifyAccount"));
const CartSuccess = lazy(() => import("./pages/CartSuccess"));
const CartError = lazy(() => import("./pages/CartError"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const CampaignSalesPage = lazy(() => import("./pages/CampaignSalesPage"));
const SubscriptionCheckout = lazy(() => import("./pages/SubscriptionCheckout"));
const JwTestPage = lazy(() => import("./pages/JwTestPage"));
const ReelShare = lazy(() => import("./pages/ReelShare"));

function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    if (
      window.__analyticsUrlSafe !== true ||
      !window.gtag ||
      !hasStatisticsConsent()
    ) return;

    window.gtag("config", "G-64FJWNDP87", {
      page_path: location.pathname + location.search,
    });
  }, [location]);
}

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { hasChecked, userData } = useSelector((state) => state.userReducer);
  const isAuthenticated = !!userData?.firstName;
  const isCampaignSalesPage = CAMPAIGN_SALES_PATHS.includes(
    location.pathname.replace(/\/$/, "")
  );
  const analyticsUrlSafe = window.__analyticsUrlSafe === true;

  usePageTracking();

  useEffect(() => {
    if (!hasChecked) {
      dispatch(checkAuth());
    }
  }, [dispatch, hasChecked]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMySubscription());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (userData?.id) {
      pushUserData(userData, null);
    }
  }, [location.pathname, location.search, userData?.id]);

  // Pamti da li je korisnik već interagovao (klik, scroll...)
  useEffect(() => {
    sessionStorage.setItem("globalMute", 1);
    const handleFirstInteraction = () => {
      sessionStorage.setItem("userInteracted", "1");
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("scroll", handleFirstInteraction);
    };

    if (!sessionStorage.getItem("userInteracted")) {
      window.addEventListener("click", handleFirstInteraction);
      window.addEventListener("scroll", handleFirstInteraction);
    }

    return () => {
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("scroll", handleFirstInteraction);
    };
  }, []);

  // if (!localStorage.getItem("katarza_muted")) {
  //   localStorage.setItem("katarza_muted", 1);
  // }

  return (
    <ModalProvider>
      <RegistrationPromptProvider disabled={isCampaignSalesPage}>
      <RegistrationPrompt />
      {analyticsUrlSafe && <Helmet>
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-64FJWNDP87"
        ></script>

        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag("js", new Date());
            if (window.localStorage.getItem("cookie_consent")) {
              try {
                var cookieConsent = JSON.parse(window.localStorage.getItem("cookie_consent"));
                if (cookieConsent.categories && cookieConsent.categories.statistics) {
                  gtag("config", "G-64FJWNDP87", {
                    page_path: window.location.pathname,
                  });
                }
              } catch (err) {}
            }
          `}
        </script>
      </Helmet>}
      <ChunkLoadErrorBoundary>
        <Suspense fallback={<div className="loader-overlay"><Spinner /></div>}>
        <Routes>
        {/* Public routes */}
        <Route
          exact
          path={"/"}
          element={<PublicRoute component={Home} />}
        ></Route>
        <Route
          exact
          path={"/about"}
          element={<PublicRoute component={About} />}
        ></Route>
        <Route
          exact
          path={"/reel/:id"}
          element={<PublicRoute component={ReelShare} />}
        ></Route>
        <Route
          exact
          path={"/contact"}
          element={<PublicRoute component={Contact} />}
        ></Route>
        <Route
          exact
          path={"/contact-instructor"}
          element={<PublicRoute component={ContactChannel} />}
        ></Route>
        <Route
          exact
          path={"/privacy-policy"}
          element={<PublicRoute component={PrivacyPolicy} />}
        ></Route>
        <Route
          exact
          path={"/terms"}
          element={<PublicRoute component={Terms} />}
        ></Route>
        <Route path="*" element={<PublicRoute component={ErrorPage} />} />
        <Route
          exact
          path={"/reels"}
          element={<PublicRoute component={ReelsLanding} />}
        ></Route>

        {/* Content routes — open for anonymous browsing */}
        <Route
          exact
          path={"/mikrokat"}
          element={<PublicRoute component={MikrokatGrid} />}
        ></Route>
        <Route
          exact
          path={"/mikrokat-grid"}
          element={<PublicRoute component={MikrokatGrid} />}
        ></Route>
        <Route
          exact
          path={"/minikat-grid"}
          element={<PublicRoute component={MinikatGrid} />}
        ></Route>
        <Route
          exact
          path={"/masterkat-grid"}
          element={<PublicRoute component={MasterkatGrid} />}
        ></Route>
        <Route
          exact
          path={"/masterkat"}
          element={<PublicRoute component={MasterkatLanding} />}
        ></Route>
        <Route
          exact
          path={"/masterkat2"}
          element={<PublicRoute component={MasterkatLanding2} />}
        ></Route>
        <Route
          exact
          path={"/minikat"}
          element={<PublicRoute component={MinikatLanding} />}
        ></Route>
        <Route
          exact
          path={"/mikrokat-player/:id"}
          element={<PublicRoute component={MikrokatPlayer} />}
        ></Route>
        <Route
          exact
          path={"/our-instructors"}
          element={<PublicRoute component={OurInstructors} />}
        ></Route>
        <Route
          exact
          path={"/instructor-profile/:id"}
          element={<PublicRoute component={InstructorProfile} />}
        ></Route>
        <Route
          exact
          path={"/masterkat-course-player/:id"}
          element={<PublicRoute component={MasterkatCoursePlayer} />}
        ></Route>
        <Route
          exact
          path={"/minikat-player/:id"}
          element={<PublicRoute component={MinikatPlayer} />}
        ></Route>
        <Route
          exact
          path={"/search"}
          element={<PublicRoute component={Search} />}
        ></Route>
        <Route
          exact
          path={CANONICAL_SALES_PATH}
          element={<Subscribe />}
        ></Route>
        {LEGACY_SALES_PATHS.map((path) => (
          <Route
            key={path}
            exact
            path={path}
            element={
              <Navigate
                replace
                to={buildCanonicalSalesPath({
                  search: location.search,
                  hash: location.hash,
                })}
              />
            }
          ></Route>
        ))}
        {CAMPAIGN_SALES_ROUTES.map(
          ({ variantKey, path }) => (
            <Route
              key={path}
              exact
              path={path}
              element={<CampaignSalesPage variantKey={variantKey} />}
            ></Route>
          )
        )}
        <Route
          exact
          path={"/subscription-checkout/:token"}
          element={<SubscriptionCheckout />}
        ></Route>
        <Route
          exact
          path={"/masterkat-player/:id"}
          element={<PublicRoute component={MasterkatPlayer} />}
        ></Route>

        {/* Private routes — require authentication */}
        <Route
          exact
          path={"/profile/:id"}
          element={<PrivateRoute component={Profile} />}
        ></Route>
        <Route
          exact
          path={"/profile/cart-detail/:idValue"}
          element={<PrivateRoute component={Profile} />}
        ></Route>
        {/* <Route
          exact
          path={"/faq"}
          element={<PublicRoute component={FaqPage} />}
        ></Route> */}
        <Route
          exact
          path="/verify-account/:urlToken"
          element={<PublicRoute component={VerifyAccount} />}
        />
        <Route
          exact
          path={"/cart-success"}
          element={<PublicRoute component={CartSuccess} />}
        ></Route>
        <Route
          exact
          path={"/cart-error"}
          element={<PublicRoute component={CartError} />}
        ></Route>
        <Route
          exact
          path={"/reset-password/:urlToken"}
          element={<PublicRoute component={ResetPassword} />}
        ></Route>
        <Route
          exact
          path={"/jw-test-page"}
          element={<PublicRoute component={JwTestPage} />}
        ></Route>
        </Routes>
        </Suspense>
      </ChunkLoadErrorBoundary>
      <FloatingSubscribeButton />
      <ScrollToTop />
      <ToastContainer
        toastClassName="custom-toast"
        bodyClassName="custom-toast-body"
        // hideProgressBar
        autoClose={5000}
      />
    </RegistrationPromptProvider>
    </ModalProvider>
  );
}

export default App;
